import React, { useEffect, useRef, useState } from 'react';
import { Search, UserPlus, X } from 'lucide-react';
import { matchesQuery } from './seatingUtils';

// Type-ahead guest picker: "becky" lists every Becky, Rebecca Beckwith, or a plus-one named Becky.
export const GuestSearch = ({ guests, onPick, onFreeText, placeholder = 'Type a name…', testId, tableLabelFor, autoFocus }) => {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);
  const box = useRef(null);
  useEffect(() => {
    const close = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);
  const results = q.trim() ? guests.filter((g) => matchesQuery(g, q)).slice(0, 30) : [];
  const pick = (g) => { onPick(g); setQ(''); setOpen(false); setHi(0); };
  const onKey = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setHi((h) => Math.min(h + 1, results.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      if (results[hi]) pick(results[hi]);
      else if (onFreeText && q.trim()) { onFreeText(q.trim()); setQ(''); setOpen(false); }
    } else if (e.key === 'Escape') setOpen(false);
  };
  return (
    <div ref={box} className="relative" data-testid={testId}>
      <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#9A8A7E]" />
      <input
        data-testid={`${testId}-input`} value={q} autoFocus={autoFocus} placeholder={placeholder} onKeyDown={onKey}
        onChange={(e) => { setQ(e.target.value); setOpen(true); setHi(0); }} onFocus={() => setOpen(true)}
        className="w-full pl-9 pr-8 py-2.5 rounded-full border border-[#EFE3D8] bg-white text-sm focus:outline-none focus:border-[#7B2C3B]"
      />
      {q && <button aria-label="Clear" onClick={() => setQ('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9A8A7E]"><X className="w-3.5 h-3.5" /></button>}
      {open && q.trim() && (
        <ul data-testid={`${testId}-results`} className="absolute z-30 mt-1 w-full max-h-72 overflow-auto rounded-2xl border border-[#EFE3D8] bg-white shadow-xl py-1">
          {results.map((g, i) => (
            <li key={g.id}>
              <button data-testid={`${testId}-option-${g.id}`} onMouseEnter={() => setHi(i)} onClick={() => pick(g)}
                className={`w-full text-left px-4 py-2 flex items-center gap-2 text-sm ${i === hi ? 'bg-[#F6EAE0]' : ''}`}>
                <span className="flex-1 min-w-0">
                  <span className="text-[#2A2A2A]">{g.name}</span>
                  {g.plusOneName && <span className="text-[11px] text-[#9A8A7E]"> &amp; {g.plusOneName}</span>}
                </span>
                {g.rsvp && g.rsvp !== 'Pending' && <span className={`text-[10px] px-1.5 rounded-full ${g.rsvp === 'Yes' ? 'bg-[#E8F5E9] text-[#2E7D32]' : 'bg-[#FFE4EC] text-[#B4235A]'}`}>{g.rsvp}</span>}
                <span className="text-[11px] text-[#9A8A7E] whitespace-nowrap">{tableLabelFor?.(g) || 'Not seated'}</span>
              </button>
            </li>
          ))}
          {!results.length && (
            onFreeText ? (
              <li><button data-testid={`${testId}-freetext`} onClick={() => { onFreeText(q.trim()); setQ(''); setOpen(false); }} className="w-full text-left px-4 py-2 text-sm text-[#7B2C3B] inline-flex items-center gap-2"><UserPlus className="w-4 h-4" /> Add “{q.trim()}” as a name only</button></li>
            ) : <li className="px-4 py-2 text-sm text-[#9A8A7E]">No guest matches “{q.trim()}”</li>
          )}
        </ul>
      )}
    </div>
  );
};

export default GuestSearch;
